import { useState, useEffect, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import api, { apiErr, oneClickCostSeconds, fmtTime, resolveVoiceById } from "../lib/api";
import { Card, btnPrimary, CostBadge } from "./ui-bits";
import VoicePickerButton from "./VoicePickerButton";
import { useQuota } from "../context/QuotaContext";
import { toast } from "sonner";
import { X, Wand2, Loader2, Music, Music2, Mic, Clock, AlertTriangle, CheckCircle2, Crown } from "lucide-react";

// Rango del slider de duración. El máximo es el del plan MÁS ALTO (Studio, 90 min — ver PLANS
// en backend/server.py), no el del plan del usuario, a propósito: el tope propio se avisa al
// pulsar (ver exceedsMaxMinutes) en vez de recortar el slider, para que el usuario VEA lo que
// se está perdiendo. Antes estaba fijo en 60, así que un usuario Studio no podía ni seleccionar
// los 90 min que paga, ni uno Pro sus 70.
const DUR_MIN = 5;
const DUR_MAX = 90;
const DUR_TICKS = [5, 30, 60, 90];
const durPct = (v, min, max) => ((v - min) / (max - min)) * 100;

/**
 * Panel "Crear vídeo en 1 clic" — formulario mínimo para arrancar el pipeline automático.
 * El usuario sólo elige título, descripción, duración, voz y música. El resto lo hace la IA:
 * genera guion, decide storyboard como director de cine, descarga B-roll multi-fuente y
 * compone. Antes de renderizar pausa para que el usuario revise el B-roll elegido.
 *
 * Inline (Card), no popup — mismo patrón que el panel "Nuevo Vídeo" manual (TabVideos.jsx,
 * new-video-panel): se abre/cierra dentro del flujo normal de la página, con scroll-into-view
 * vía ref, en vez de tapar la pantalla con un overlay. Antes era un modal fijo a pantalla
 * completa; cambiado ago-2026 a petición explícita para que ambos caminos de creación (manual
 * y 1-clic) se sientan como el mismo tipo de superficie.
 *
 * Props:
 *   channelId: string — id del canal en el que se creaba el vídeo.
 *   channel: object — el canal completo (para leer default_voice, language, etc.).
 *   sourceKind: "manual" | "idea" | "calendar" — origen del vídeo (para trazabilidad).
 *   sourceRef: string — id del origen (idea_id, slot_id…).
 *   initialTitle, initialDescription: para pre-rellenar cuando viene de una idea o slot.
 *   projectId: si viene informado, el guion YA EXISTE (pegado a mano, mejorado con IA…) —
 *     el panel oculta título/descripción/duración (no hay nada que pedir, el guion ya los
 *     fija) y arranca el pipeline sobre ESE proyecto en vez de crear uno nuevo. Sustituye al
 *     antiguo botón "Auto-producir" de la pestaña de Guion.
 *   estSeconds: duración estimada del guion ya escrito (solo se usa en modo projectId, como
 *     valor de respaldo del badge de coste mientras llega la cifra real del backend).
 *   onClose(): cierra el panel.
 *   onCreated(projectId): callback con el pid recién creado → el padre navega al producer.
 */
const OneClickModal = forwardRef(function OneClickModal({
  channelId, channel, sourceKind = "manual", sourceRef = "",
  initialTitle = "", initialDescription = "", projectId = "", estSeconds = 0,
  onClose, onCreated,
}, ref) {
  const { t } = useTranslation();
  const { quota, openUpgrade } = useQuota() || {};
  const reuseScript = !!projectId;
  const durationMin = DUR_MIN;
  const durationMax = DUR_MAX;
  const durationTicks = DUR_TICKS;
  const [titulo, setTitulo] = useState(initialTitle);
  const [descripcion, setDescripcion] = useState(initialDescription);
  const [duracion, setDuracion] = useState(20);   // ver DUR_MIN/DUR_MAX arriba — sin tope de
  // plan en el propio slider a propósito: se deja marcar libremente y el tope del plan (además
  // del saldo, ver insufficientBalance más abajo) se comprueba al pulsar el botón, no
  // recortando el slider — así el usuario ve lo que se perdería y el aviso genera necesidad
  // real de mejorar de plan en vez de ocultarle la opción desde el principio.
  const [voice, setVoice] = useState("");         // vacío = default del canal
  const [defaultVoiceName, setDefaultVoiceName] = useState("");
  const [defaultVoiceResolved, setDefaultVoiceResolved] = useState(false);
  const [music, setMusic] = useState(true);
  // Estas tres decisiones dejaron de preguntarse (ago-2026): el 1-clic va SIEMPRE en supermega,
  // SIEMPRE para a que revises antes de gastar el render, y NUNCA genera la miniatura sola.
  //   · supermega dejó de ser un modo de prueba y pasó a ser el criterio editorial normal.
  //   · la parada de revisión es lo que hace barato equivocarse: revisar es gratis, renderizar no.
  //   · la miniatura se crea desde la pantalla de publicación, junto a la subida a YouTube, que
  //     es donde de verdad se decide (allí se ve, se regenera y se sustituye — ver PublishStage).
  // El backend sigue soportando los tres caminos por parámetro; solo desaparecen del formulario.
  const autoRender = false;
  const autoThumbnail = false;
  const supermega = true;
  const [busy, setBusy] = useState(false);
  // En modo "reutilizar guion" el coste incluye, además del render, el análisis de storyboard
  // con IA para las escenas que no lo traigan ya (ver extract_storyboard_for_segments en el
  // backend) — el badge de coste local (oneClickCostSeconds) no lo sabe calcular porque
  // depende de CUÁNTAS escenas del proyecto real ya tienen storyboard, así que se pide al
  // backend la cifra exacta en cuanto se conoce el projectId.
  const [reuseScriptCost, setReuseScriptCost] = useState(Math.max(60, estSeconds));
  useEffect(() => {
    if (!reuseScript) return;
    let cancel = false;
    api.post("/cost/estimate", { action: "one_click_from_script", project_id: projectId })
      .then(({ data }) => { if (!cancel && data?.seconds != null) setReuseScriptCost(data.seconds); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [reuseScript, projectId]);

  const currentCost = reuseScript ? reuseScriptCost : oneClickCostSeconds(duracion);
  const insufficientBalance = !!quota && quota.seconds_remaining < currentCost;
  // El plan Gratis no puede recargar saldo (esa opción solo existe para planes de pago, ver
  // ProfileMenu.jsx) — sugerirle "recarga saldo" ahí es una opción que no existe para él.
  const insufficientBalanceMsg = quota && t(
    quota.plan === "gratis" ? "oc.insufficientBalanceFree" : "oc.insufficientBalance",
    { remaining: fmtTime(quota.seconds_remaining), needed: fmtTime(currentCost) });

  // Tope de duración por plan: igual que el saldo, no restringe el slider — se avisa al
  // pulsar el botón, con mensaje de mejora de plan (ver comentario del slider más arriba).
  const exceedsMaxMinutes = !reuseScript && !!quota && duracion > quota.max_minutes;
  const exceedsMaxMinutesMsg = exceedsMaxMinutes && t("oc.exceedsMaxMinutes", { max: quota.max_minutes });

  useEffect(() => {
    // Nombre de la voz por defecto del canal, para el botón fijo "usar la de siempre" — ya no
    // hay una lista propia en la que buscarlo (ver VoiceSelect, el catálogo de ai33).
    // `resolved` distingue "todavía cargando" de "no está en el catálogo": hay canales con una
    // voz guardada que ya no existe en ai33 (verificado en producción — voces del motor gratuito
    // antiguo tipo "es-ES-AlvaroNeural", o clonadas que nunca se subieron). Sin esa distinción
    // se enseñaba el ID crudo como si fuera el nombre de la voz.
    let cancel = false;
    setDefaultVoiceResolved(false);
    if (!channel?.default_voice) { setDefaultVoiceName(""); return; }
    resolveVoiceById(channel.default_voice, channel?.language || "es").then((v) => {
      if (cancel) return;
      setDefaultVoiceName(v?.name || "");
      setDefaultVoiceResolved(true);
    });
    return () => { cancel = true; };
  }, [channel?.default_voice, channel?.language]);

  const defaultVoiceLabel = !channel?.default_voice
    ? t("oc.defaultVoiceGeneric")
    : defaultVoiceName
    ? t("oc.defaultVoiceNamed", { name: defaultVoiceName })
    : defaultVoiceResolved
    ? t("oc.defaultVoiceMissing")   // guardada pero ya no está en el catálogo
    : t("oc.defaultVoiceLoading");

  const submit = async () => {
    const tt = titulo.trim();
    if (!reuseScript && tt.length < 6) { toast.warning(t("oc.titleTooShort")); return; }
    setBusy(true);
    try {
      const { data } = reuseScript
        ? await api.post(`/projects/${projectId}/one-click/from-script`, { voice, music, auto_render: autoRender, auto_thumbnail: autoThumbnail, supermega })
        : await api.post("/videos/one-click", {
            channel_id: channelId,
            titulo: tt,
            descripcion: descripcion.trim(),
            duracion_min: duracion,
            voice,
            music,
            auto_render: autoRender,
            auto_thumbnail: autoThumbnail,
            supermega,
            source_kind: sourceKind,
            source_ref: sourceRef,
          });
      toast.success(t("oc.started"));
      onCreated && onCreated(data.project_id, tt);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card ref={ref} data-testid="oneclick-panel" className="p-6 sm:p-8 border-brand-100 scroll-mt-24">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-400 to-brand-700 flex items-center justify-center shadow-md shrink-0">
            <Wand2 size={22} className="text-white" />
          </div>
          <div>
            <h3 className="text-2xl font-bold font-head text-slate-900 flex items-center gap-2">
              {reuseScript ? t("oc.reuseScriptTitle") : t("oc.title")}
            </h3>
            <p className="text-sm text-slate-600">{reuseScript ? t("oc.reuseScriptSubtitle") : t("oc.subtitle")}</p>
          </div>
        </div>
        <button data-testid="oneclick-close" onClick={onClose} className="shrink-0 text-slate-400 hover:text-slate-700"><X size={26} /></button>
      </div>

      <div className="space-y-5">
            {!reuseScript && (<>
            {/* TÍTULO */}
            <div>
              <label className="block text-base font-semibold text-slate-800 mb-2">{t("oc.videoTitle")}</label>
              <input
                data-testid="oneclick-title-input"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                maxLength={120}
                placeholder={t("oc.videoTitlePlaceholder")}
                className="w-full border-2 border-slate-300 rounded-lg px-4 py-3 text-lg focus:border-brand-600 focus:ring-4 focus:ring-brand-600/20 outline-none"
              />
              <div className="text-xs text-slate-500 mt-1">{t("oc.charsCount", { count: titulo.length, max: 120 })}</div>
            </div>

            {/* DESCRIPCIÓN */}
            <div>
              <label className="block text-base font-semibold text-slate-800 mb-2">{t("oc.description")} <span className="text-slate-500 font-normal">{t("oc.optional")}</span></label>
              <textarea
                data-testid="oneclick-desc-input"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder={t("oc.descriptionPlaceholder")}
                className="w-full border-2 border-slate-300 rounded-lg px-4 py-3 text-base focus:border-brand-600 focus:ring-4 focus:ring-brand-600/20 outline-none resize-none"
              />
              <div className="text-xs text-slate-500 mt-1">{t("oc.charsCount", { count: descripcion.length, max: 500 })}</div>
            </div>

            {/* DURACIÓN */}
            <div>
              <label className="block text-base font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <Clock size={18} /> {t("oc.targetDuration")}
                <span className="text-brand-600 font-bold text-lg ml-auto">{t("oc.minutes", { count: duracion })}</span>
              </label>
              <input
                type="range" min={durationMin} max={durationMax} step={5}
                value={duracion}
                onChange={(e) => setDuracion(parseInt(e.target.value, 10))}
                className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-brand-600"
                data-testid="oneclick-duration-slider"
              />
              {/* Etiquetas colocadas por su POSICIÓN REAL en el rango, no repartidas con
                  justify-between (bug real reportado): con min=5 la marca de 30 min no cae al
                  33% del ancho sino al 29,4%, así que el tirador nunca coincidía con su
                  etiqueta. La primera y la última se anclan a los bordes en vez de centrarse,
                  para que no se salgan del contenedor. */}
              <div className="relative h-5 mt-1 text-xs text-slate-500">
                {durationTicks.map((v, i) => {
                  const isFirst = i === 0;
                  const isLast = i === durationTicks.length - 1;
                  const style = isFirst ? { left: 0 } : isLast ? { right: 0 } : { left: `${durPct(v, durationMin, durationMax)}%` };
                  return (
                    <span key={v} className={`absolute whitespace-nowrap ${isFirst || isLast ? "" : "-translate-x-1/2"}`} style={style}>
                      {t("oc.minutes", { count: v })}
                    </span>
                  );
                })}
              </div>
              {exceedsMaxMinutes && (
                <div data-testid="oneclick-exceeds-max-minutes" className="mt-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                  <AlertTriangle size={16} className="shrink-0" />
                  <span className="flex-1 min-w-[180px]">{exceedsMaxMinutesMsg}</span>
                  {openUpgrade && (
                    <button type="button" data-testid="oneclick-upgrade-minutes"
                      onClick={() => openUpgrade({ msg: exceedsMaxMinutesMsg })}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-600 text-white text-sm font-bold hover:bg-orange-700">
                      <Crown size={15} /> {t("oc.upgradePlan")}
                    </button>
                  )}
                </div>
              )}
              {!exceedsMaxMinutes && insufficientBalance && (
                <div data-testid="oneclick-insufficient-balance" className="mt-2 flex items-start gap-2 text-sm font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                  <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                  {insufficientBalanceMsg}
                </div>
              )}
            </div>
            </>)}


            {/* VOZ */}
            <div>
              <label className="block text-base font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <Mic size={18} /> {t("oc.narratorVoice")}
              </label>
              <button type="button" data-testid="oneclick-voice-default" onClick={() => setVoice("")}
                className={`w-full text-left border-2 rounded-lg p-3 mb-2 transition-colors bg-white flex items-center justify-between gap-2 ${voice === "" ? "border-brand-600 bg-brand-50" : "border-slate-200 hover:border-slate-300"}`}>
                <span className="font-semibold text-slate-800">{defaultVoiceLabel}</span>
                {voice === "" && <CheckCircle2 size={20} className="text-brand-600 shrink-0" />}
              </button>
              <VoicePickerButton language={channel?.language || "es"} value={voice} onChange={(v) => setVoice(v.id)} />
            </div>

            {/* MÚSICA — con la opción activa, la IA elige automáticamente pistas de la biblioteca
                YTMethod según el tono del vídeo (misterio, épica, tensión…). El usuario puede
                cambiarlas después en la pestaña Música del editor. "Sin música" fuerza
                music_tracks=[] y se salta la fase de BGM aunque haya pistas en el proyecto. */}
            <div>
              <label className="block text-base font-semibold text-slate-800 mb-2 flex items-center gap-2">
                {music ? <Music2 size={18} /> : <Music size={18} />} {t("oc.backgroundMusic")}
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  data-testid="oneclick-music-on"
                  onClick={() => setMusic(true)}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 text-base font-semibold transition-colors ${music ? "border-brand-600 bg-brand-50 text-brand-700" : "border-slate-300 text-slate-600 hover:border-slate-400"}`}
                >
                  {t("oc.musicAuto")}
                </button>
                <button
                  type="button"
                  data-testid="oneclick-music-off"
                  onClick={() => setMusic(false)}
                  className={`flex-1 px-4 py-3 rounded-lg border-2 text-base font-semibold transition-colors ${!music ? "border-brand-600 bg-brand-50 text-brand-700" : "border-slate-300 text-slate-600 hover:border-slate-400"}`}
                >
                  {t("oc.musicOff")}
                </button>
              </div>
            </div>

            {reuseScript && insufficientBalance && (
              <div data-testid="oneclick-insufficient-balance" className="flex items-start gap-2 text-sm font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                {insufficientBalanceMsg}
              </div>
            )}
      </div>

      {/* BOTONES — dentro del flujo normal de la Card, como el panel manual. En columna en móvil:
          el de crear lleva icono + texto + CostBadge, y a medio ancho junto a "Cancelar" no
          entraba (el badge de créditos se salía de la pantalla). */}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 mt-6 border-t border-slate-200">
        <button onClick={onClose} disabled={busy} className="order-2 sm:order-1 sm:flex-1 min-h-[48px] px-4 sm:px-5 text-base rounded-xl border-2 border-slate-300 text-slate-700 font-bold hover:bg-slate-50">
          {t("oc.cancel")}
        </button>
        <button
          data-testid="oneclick-submit"
          onClick={submit}
          disabled={busy || (!reuseScript && titulo.trim().length < 6) || insufficientBalance || exceedsMaxMinutes}
          className={`order-1 sm:order-2 sm:flex-1 ${btnPrimary} justify-center bg-gradient-to-br from-brand-400 to-brand-700 hover:brightness-90 disabled:opacity-60 disabled:cursor-not-allowed`}
        >
          {busy ? (<><Loader2 size={20} className="animate-spin" /> {t("oc.starting")}</>)
                : (<><Wand2 size={20} /> <span className="whitespace-nowrap">{t("oc.submitBtn")}</span></>)}
          {!busy && (
            <CostBadge
              seconds={currentCost}
              tip={reuseScript ? t("oc.reuseScriptCostTip") : t("oc.costTipManual")}
            />
          )}
        </button>
      </div>
    </Card>
  );
});

export default OneClickModal;
